import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import './App.css';
import { NavBar } from './components/ui/TubelightNavbar.jsx';
import { Logo } from './components/Logo';
import { LandingPage } from './components/LandingPage';

import { ChatPage } from './components/ChatPage';
import { ImageGenerationWorkspace } from './components/ImageGenerationWorkspace';
import { VideoGenerationWorkspace } from './components/VideoGenerationWorkspace';
import { MusicGenerationWorkspace } from './components/MusicGenerationWorkspace';
import { MusicVideoWorkspace } from './components/MusicVideoWorkspace';
import { WebsiteBuilderWorkspace } from './components/WebsiteBuilderWorkspace';
import { UnifiedWorkspace } from './workspace/UnifiedWorkspace';
import { ModelsPage } from './components/ModelsPage';
import { SolutionsPage } from './components/SolutionsPage';
import { EnterprisePage } from './components/EnterprisePage';
import { DashboardPage } from './components/DashboardPage';
import { PrivacyPolicyPage } from './components/PrivacyPolicyPage';
import { TermsOfServicePage } from './components/TermsOfServicePage';
import { CookiePolicyPage } from './components/CookiePolicyPage';
import { PreviewPage } from './components/PreviewPage';
import { ModelPerformance } from './components/ModelPerformance';
import { CommunityShowcase } from './components/CommunityShowcase';
import { Footer } from './components/ui/Footer';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { WebContainerProvider } from './components/WebContainerContext';
import { AdminConsole } from './components/AdminConsole';
import { ModelProvider } from './contexts/ModelContext';
import { AuthProvider } from './contexts/AuthContext';
import LoginModal from './components/auth/LoginModal';
import ProtectedRoute from './components/auth/ProtectedRoute';
import GoogleCallbackPage from './components/auth/GoogleCallbackPage';
import DebugAuthPage from './components/auth/DebugAuthPage';
import SSOPage from './components/auth/SSOPage';

function StatefulRedirect({ to }) {
  const location = useLocation();
  return <Navigate to={to} state={location.state} replace />;
}

const navItems = [
  { name: 'Home', url: '/', icon: 'home' },
  { name: 'Solutions', url: '/solutions' },
  { name: 'Models', url: '/models' },
  { name: 'Enterprise', url: '/enterprise' },
];

function HomePage() {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [redirectPath, setRedirectPath] = useState('/dashboard');

  const handleSignInClick = React.useCallback((target) => {
    if (target && typeof target === 'string') {
      setRedirectPath(target);
    } else {
      setRedirectPath('/dashboard');
    }
    setShowLoginModal(true);
  }, []);

    return (
      <div className="min-h-screen w-full relative text-slate-900 selection:bg-purple-500/10">
          {/* Logo */}
          <div className="fixed top-4 left-4 md:top-6 md:left-8 z-[100]">
            <Logo />
          </div>
          
          {/* Navbar */}
          <div className="relative z-[100]">
            <NavBar items={navItems} onSignInClick={handleSignInClick} />
          </div>
          
          {/* Cinematic Scroll Landing (Full Experience) */}
          <LandingPage onSignInClick={handleSignInClick} />
        
        {/* Login Modal */}
        {showLoginModal && (
          <LoginModal 
            isOpen={showLoginModal} 
            onClose={() => setShowLoginModal(false)} 
            redirectPath={redirectPath}
          />
        )}
      </div>
    );
}

function App() {
  return (
    <AuthProvider>
      <ModelProvider>
        <WebContainerProvider>
          <Router>
            <Routes>
              {/* ── Public routes ── */}
              <Route path="/" element={<HomePage />} />
              <Route path="/models" element={<ModelsPage />} />
              <Route path="/solutions" element={<SolutionsPage />} />
              <Route path="/enterprise" element={<EnterprisePage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/cookie-policy" element={<CookiePolicyPage />} />
              <Route path="/preview/:id" element={<PreviewPage />} />
              <Route path="/admin" element={<AdminConsole />} />
              <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
              <Route path="/sso" element={<SSOPage />} />
              <Route path="/debug-auth" element={<DebugAuthPage />} />

              {/* ── Protected routes (require login) ── */}
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/chat" element={<ProtectedRoute><StatefulRedirect to="/workspace/chat" /></ProtectedRoute>} />
              <Route path="/image-generation" element={<ProtectedRoute><StatefulRedirect to="/workspace/image" /></ProtectedRoute>} />
              <Route path="/video-generation" element={<ProtectedRoute><StatefulRedirect to="/workspace/video" /></ProtectedRoute>} />
              <Route path="/music-generation" element={<ProtectedRoute><StatefulRedirect to="/workspace/music" /></ProtectedRoute>} />
              <Route path="/music-videos" element={<ProtectedRoute><StatefulRedirect to="/workspace/music" /></ProtectedRoute>} />
              <Route path="/website-builder" element={<ProtectedRoute><WebsiteBuilderWorkspace /></ProtectedRoute>} />

              {/* ── Unified Workspace (Protected) ── */}
              <Route path="/workspace" element={<ProtectedRoute><UnifiedWorkspace /></ProtectedRoute>} />
              <Route path="/workspace/:mode" element={<ProtectedRoute><UnifiedWorkspace /></ProtectedRoute>} />
            </Routes>
          </Router>
        </WebContainerProvider>
      </ModelProvider>
    </AuthProvider>
  );
}

export default App;
